import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, CTA, Richtext } from '@volkswagen-onehub/components-core';
import {
  StyledCheckBoxContainer,
  StyledTosWrapper,
  StyledSubmitButtonContainer,
  StyledLegalContainer,
} from '../styles/tos-styles';
import { onVWBasicFormStepButtonClickEvent, onVWBasicFormStepLoadEvent } from '../../../shared/tagging/tagging-helpers';
import { useFormErrorHandling } from '../../../shared/hooks/use-form-error-handling';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import tosEnCA from '../../../legal/tos-en-ca';
import tosFrCA from '../../../legal/tos-fr-ca';
import { useFormik } from 'formik';

interface TermsOfServiceProps {
  onComplete: () => void;
  initialScrollComplete?: boolean;
}

export const TermsOfService = ({ onComplete, initialScrollComplete = false}: TermsOfServiceProps) => {
  const { authService, translationHandler } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();
  const isFrench = localeService?.language === 'fr';
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const { setIsSubmitting, handleSubmitError } = useFormErrorHandling({ authService });
  const trackingManager = useTrackingManager();

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState<boolean>(initialScrollComplete);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkScrollEnd = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, clientHeight, scrollHeight } = container;
    const isEnd = scrollTop + clientHeight >= scrollHeight - 1;
    if (isEnd && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', checkScrollEnd);
    checkScrollEnd();
    return () => container.removeEventListener('scroll', checkScrollEnd);
  }, []);

  useEffect(() => {
    onVWBasicFormStepLoadEvent(trackingManager, {});
  }, []);

  const handleFormNextTracking = () => {
    onVWBasicFormStepButtonClickEvent(trackingManager, {
      Type: 'Button',
      Name: translationHandler('registration.next.cta.label'),
    });
  };

  const formik = useFormik({
    initialValues: {
      acceptedTos: false,
    },
    onSubmit: async (values) => {
      if (!hasScrolledToEnd || !values.acceptedTos) return;

      setIsSubmitting(true);
      try {
        await authService.saveUserMyVWTosResponse(localeCode, "myVW", "Accept");
        handleFormNextTracking();
        onComplete();
      } catch (error) {
        handleSubmitError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
  });

  const handleCheckboxChange = (e: any) => {
    if (!hasScrolledToEnd) return;
    const isChecked = e.target?.checked ?? false;
    formik.setFieldValue('acceptedTos', isChecked);
  };

  const TOSCheckBox = () => (
    <StyledCheckBoxContainer>
      <Checkbox
        aria-required="true"
        aria-label={translationHandler('registration.termsofservice.checkbox')}
        label={<>{translationHandler('registration.termsofservice.checkbox')}</>}
        required
        onChange={handleCheckboxChange}
        checked={formik.values.acceptedTos}
        disabled={!hasScrolledToEnd}
        value={String(formik.values.acceptedTos)}
        name="acceptedTos"
      />
    </StyledCheckBoxContainer>
  );

  return (
    <StyledTosWrapper>
      <StyledLegalContainer ref={scrollContainerRef}>
        <Richtext
          markdown={isFrench ? tosFrCA : tosEnCA}
          dangerouslyParseRawHTML
        />
      </StyledLegalContainer>
      <form onSubmit={formik.handleSubmit}>
        <TOSCheckBox />
        <StyledSubmitButtonContainer>
          <CTA
            aria-label="Submit button"
            emphasis="primary"
            stretchContent
            tag="button"
            type="submit"
            disabled={!formik.values.acceptedTos}
          >
            {translationHandler('registration.next.cta.label')}
          </CTA>
        </StyledSubmitButtonContainer>
      </form>
    </StyledTosWrapper>
  );
};